import { useState, useRef, useCallback, useEffect } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import star from "../../../images/star.svg";
import { ANNOUNCEMENTS } from "../../announcements/announcements.constants";
import AssistantOptions from "../AssistantOptions";
import ChatInput from "./ChatInput";
import ChatMessages from "./ChatMessages";
import InMeetingExpFeedbackPopover from "../InMeetingExpFeedbackPopover";
import AnnouncementToast from "../../announcements/AnnouncementToast";
import LogManager from "../../../utils/logManager";
import { invokeAgentStream } from "../assistant.utils";
import { fetchWhosWho, parseExternalInvitees } from "../../whosWho/whosWho.utils";
import { THEME_COLOR } from "../../../styles/colors";
import { PROMPT_TYPE } from "../assistant.constants";
import { useSession } from "../../../context/SessionContext";
import { useFeatureFlags } from "../../../context/FeatureFlagsContext";

const Assistant = ({
  showBanner,
  setShowBanner,
  chatMessages,
  setChatMessages,
  isChatLoading,
  setIsChatLoading,
  inviteeAliases,
  externalInvitees,
}) => {
  const { meetingUuid, meetingId, rtmsStreamId, alias, rtmsStartTime, rtmsRequesterName } = useSession();
  const { isWhosWhoEnabled } = useFeatureFlags();
  const [showOptions, setShowOptions] = useState(true);
  const [feedbackPopoverOpen, setFeedbackPopoverOpen] = useState(false);
  const feedbackSubmitRef = useRef(null);
  const sessionIdRef = useRef(`${alias || "user"}-${Date.now()}-${crypto.randomUUID()}`);
  const abortControllerRef = useRef(null);

  const dismissBanner = () => {
    setShowBanner(false);
  };
  // TODO: Remove this when queueing fucntionality is added
  const handleWhosWhoAnnouncementAction = () => {
    if (!isChatLoading) {
      handleSend("Who's who", PROMPT_TYPE.WHOS_WHO);
      return;
    }
    // If Who's Who is already in flight, skip — it will complete on its own.
    if (chatMessages.some((msg) => msg.whosWho && msg.isLoading)) return;
    // Otherwise an agent stream is in flight — abort it and queue Who's Who.
    abortControllerRef.current?.abort();
    pendingWhosWhoRef.current = true;
  };

  // After an abort settles and isChatLoading flips to false, fire the pending Who's Who.
  const pendingWhosWhoRef = useRef(false);
  useEffect(() => {
    if (!isChatLoading && pendingWhosWhoRef.current) {
      pendingWhosWhoRef.current = false;
      handleSend("Who's who", PROMPT_TYPE.WHOS_WHO);
    }
  }, [isChatLoading]);

  const handleFeedbackMessage = (message) => {
    setChatMessages((prev) => [...prev, { role: "agent", text: message }]);
  };

  const handleOpenFeedbackPopover = useCallback((submitCallback) => {
    feedbackSubmitRef.current = submitCallback;
    setFeedbackPopoverOpen(true);
  }, []);

  const handleFeedbackPopoverSubmit = (comment) => {
    if (feedbackSubmitRef.current) {
      feedbackSubmitRef.current(comment);
    }
  };

  const handleFeedbackPopoverClose = () => {
    setFeedbackPopoverOpen(false);
    feedbackSubmitRef.current = null;
  };

  const handleSend = async (message, promptType = PROMPT_TYPE.FREEFORM) => {
    if (isChatLoading) return;

    if (promptType === PROMPT_TYPE.FREEFORM) {
      LogManager.metric("Assistant_Freeform_Submitted", 1);
    }

    // Who's Who takes a separate path — calls /whos-who directly instead of /invokeAgent
    if (promptType === PROMPT_TYPE.WHOS_WHO) {
      setShowOptions(false);
      setShowBanner(false);
      setIsChatLoading(true);
      setChatMessages((prev) => [
        ...prev,
        { role: "user", text: message, promptType, createdAt: Date.now() },
        { role: "agent", whosWho: true, isLoading: true, createdAt: Date.now() },
      ]);
      const startedAt = Date.now();

      try {
        const result = await fetchWhosWho({
          meetingUuid: meetingUuid || "",
          attendees: inviteeAliases || "",
          externalInvitees: externalInvitees || "",
        });
        setChatMessages((prev) => prev.map((msg) =>
          msg.whosWho && msg.isLoading
            ? { ...msg, isLoading: false, invitees: result.invitees || [] }
            : msg
        ));
        LogManager.metric("Assistant_WhosWho_Success", 1, alias ? { alias } : {});
        LogManager.metric("Assistant_WhosWho_Latency", Date.now() - startedAt,
          alias ? { alias } : {});
      } catch (err) {
        LogManager.error("Who's Who call failed:", err);
        LogManager.metric("Assistant_WhosWho_Error", 1, alias ? { alias } : {});
        const fallbackInvitees = [
          ...(inviteeAliases || "")
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
            .map((a) => ({ id: a, alias: a, name: a, initials: a.slice(0, 2).toUpperCase() })),
          ...parseExternalInvitees(externalInvitees),
        ];
        setChatMessages((prev) => prev.map((msg) =>
          msg.whosWho && msg.isLoading
            ? { ...msg, isLoading: false, isError: true, invitees: fallbackInvitees }
            : msg
        ));
      } finally {
        setIsChatLoading(false);
      }
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setShowOptions(false);
    setShowBanner(false);
    setChatMessages((prev) => [...prev, { role: "user", text: message, promptType }]);
    setIsChatLoading(true);

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      LogManager.info(`Resolved user timezone: ${timezone}`);
      const requestBody = {
        prompt: message,
        sessionId: sessionIdRef.current,
        meetingMeta: {
          meetingId: meetingId || "",
          meetingUuid: meetingUuid || "",
          rtmsStreamId: rtmsStreamId || "",
          rtmsStartTime,
          timezone,
        },
      };
      await invokeAgentStream({ requestBody, signal: controller.signal, setChatMessages, alias });
    } catch (err) {
      if (err.name === "AbortError") return;
      LogManager.error("Agent call failed:", err);
      LogManager.metric("Assistant_ServerError_Shown", 1, alias ? { alias } : {});
      setChatMessages((prev) => {
        if (prev.length === 0) return prev;
        const updated = [...prev];
        const lastMsg = updated[updated.length - 1];
        if (lastMsg && lastMsg.role === "agent" && !lastMsg.text) {
          updated[updated.length - 1] = { ...lastMsg, isError: true };
        } else {
          updated.push({ role: "agent", text: "", isError: true });
        }
        return updated;
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleNewSession = () => {
    abortControllerRef.current?.abort();
    setShowOptions(true);
    setChatMessages([]);
    setIsChatLoading(false);
  };

  const hasMessages = chatMessages.length > 0;

  return (
    <Column spacing="none" height="calc(100vh - 131px)" heights={["fill", "fit"]}>
      <Column spacing="none" overflowY="auto">
        <Theme tokens={{ alertLinkColorMedium: THEME_COLOR.TEAL_GREEN }}>
          <AnnouncementToast
            announcement={ANNOUNCEMENTS.WHOS_WHO}
            onAction={handleWhosWhoAnnouncementAction}
            visible={isWhosWhoEnabled}
          />
        </Theme>
        {showBanner && (
          <>
            <Row height="20px"/>
            <Row alignmentHorizontal="center" spacing="none">
              <div style={{ position: 'relative' }}>
                <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.LIGHT_TEAL }}>
                  <Box type="outline" width="252px" spacingInset="400">
                    <Row spacing="none" alignmentVertical="top">
                      <img src={star} alt="star" />
                      <Column width="8px" />
                      <Column spacing="none">
                        <Text type="h50" alignment="start">
                          {rtmsRequesterName}
                        </Text>
                        <Text type="b200" alignment="start">
                          turned on meeting summary
                        </Text>
                      </Column>
                    </Row>
                  </Box>
                </Theme>
                <div style={{ position: 'absolute', top: '2px', right: '2px' }}>
                  <Button type="icon" size="small" onClick={dismissBanner}>
                    <Icon tokens={closeSmallTokens} color={THEME_COLOR.MID_GRAY}>Close</Icon>
                  </Button>
                </div>
              </div>
            </Row>
          </>
        )}
        {hasMessages && (
          <Row alignmentHorizontal="center" spacing="none">
            <ChatMessages
              messages={chatMessages}
              isLoading={isChatLoading}
              sessionId={sessionIdRef.current}
              aimsUuid={rtmsStreamId}
              meetingId={meetingId}
              meetingUuid={meetingUuid}
              onFeedbackMessage={handleFeedbackMessage}
              onOpenFeedbackPopover={handleOpenFeedbackPopover}
            />
          </Row>
        )}
      </Column>

      <Row alignmentHorizontal="center" spacing="none">
        <Column spacing="none">
          {showOptions && !hasMessages && (
            <>
              <AssistantOptions onOptionSelect={handleSend} alias={alias} isWhosWhoEnabled={isWhosWhoEnabled} />
              <Row height="8px" />
            </>
          )}
          {feedbackPopoverOpen && (
            <InMeetingExpFeedbackPopover
              onSubmit={handleFeedbackPopoverSubmit}
              onClose={handleFeedbackPopoverClose}
            />
          )}
          <ChatInput onSend={handleSend} onNewSession={handleNewSession} disabled={isChatLoading} />
          <Row height="12px" />
        </Column>
      </Row>
    </Column>
  );
};

export default Assistant;
